// ============================================
// Manor Miser - Simple BFS Pathfinder
// ============================================
// Finds shortest path on a 2D boolean walkable grid.
// Returns array of {col, row} waypoints including start and end.

export interface TileCoord {
  col: number;
  row: number;
}

/**
 * BFS pathfinding on a grid.
 * @param walkable 2D boolean grid [row][col] — true = passable
 * @param start Starting tile coordinate
 * @param end Target tile coordinate
 * @returns Array of tile coordinates from start to end (inclusive), or empty if no path
 */
export function findPath(
  walkable: boolean[][],
  start: TileCoord,
  end: TileCoord,
): TileCoord[] {
  const rows = walkable.length;
  const cols = walkable[0]?.length ?? 0;

  if (!inBounds(start, rows, cols) || !inBounds(end, rows, cols)) return [];
  if (start.col === end.col && start.row === end.row) return [start];

  // BFS
  const visited = Array.from({ length: rows }, () => new Array(cols).fill(false));
  const parent = Array.from({ length: rows }, () => new Array<TileCoord | null>(cols).fill(null));

  const queue: TileCoord[] = [start];
  visited[start.row][start.col] = true;

  const dirs = [
    { dr: -1, dc: 0 }, // north
    { dr: 1, dc: 0 },  // south
    { dr: 0, dc: -1 }, // west
    { dr: 0, dc: 1 },  // east
  ];

  while (queue.length > 0) {
    const cur = queue.shift()!;

    for (const { dr, dc } of dirs) {
      const nr = cur.row + dr;
      const nc = cur.col + dc;

      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      if (visited[nr][nc]) continue;
      if (!walkable[nr][nc]) continue;

      visited[nr][nc] = true;
      parent[nr][nc] = cur;

      if (nr === end.row && nc === end.col) {
        // Reconstruct path
        return reconstructPath(parent, start, end);
      }

      queue.push({ col: nc, row: nr });
    }
  }

  return []; // no path found
}

function reconstructPath(
  parent: (TileCoord | null)[][],
  start: TileCoord,
  end: TileCoord,
): TileCoord[] {
  const path: TileCoord[] = [];
  let cur: TileCoord | null = end;

  while (cur && !(cur.col === start.col && cur.row === start.row)) {
    path.unshift(cur);
    cur = parent[cur.row][cur.col];
  }

  path.unshift(start);
  return path;
}

function inBounds(t: TileCoord, rows: number, cols: number): boolean {
  return t.row >= 0 && t.row < rows && t.col >= 0 && t.col < cols;
}

/**
 * Simplify a path into straight-line segments (waypoints where direction changes).
 * Input: full BFS path (every tile). Output: just the corner/turn points.
 */
export function simplifyPath(path: TileCoord[]): TileCoord[] {
  if (path.length <= 2) return [...path];

  const waypoints: TileCoord[] = [path[0]];

  for (let i = 1; i < path.length - 1; i++) {
    const prev = path[i - 1];
    const cur = path[i];
    const next = path[i + 1];

    // Direction changed?
    const dr1 = cur.row - prev.row;
    const dc1 = cur.col - prev.col;
    const dr2 = next.row - cur.row;
    const dc2 = next.col - cur.col;

    if (dr1 !== dr2 || dc1 !== dc2) {
      waypoints.push(cur);
    }
  }

  waypoints.push(path[path.length - 1]);
  return waypoints;
}
